import { useControls } from "leva";
import { useRef, useEffect } from "react";
import { useGLTF, Html } from "@react-three/drei";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import HologramMaterial from "../Materials/HologramMaterial/HologramMaterial";
import { useSelector, useDispatch } from "react-redux";
import { setCurrentModule } from "../../store/reducers/progressionReducer";

export default function Holograms(props) {
  const currentModule = useSelector((state) => state.progression.currentModule);
  const currentState = useSelector((state) => state.progression.currentState);
  const dispatch = useDispatch();
  const groupRef = useRef();
  const comparatifRef = useRef();

  const { nodes, materials } = useGLTF("/saviol-recherches-desk3.glb");

  const handleComparatifClick = () => {
    dispatch(setCurrentModule("comparatif"));
  };

  // Contrôles Leva pour ajuster la rotation des hologrammes

  // Animation initiale du cube comparatif quand on entre dans TechnologyHub
  useEffect(() => {
    if (comparatifRef.current) {
      if (currentState === "TechnologyHub") {
        // Afficher le cube
        gsap.to(comparatifRef.current.scale, {
          x: 0.3,
          y: 0.3,
          z: 0.3,
          duration: 0.75,
          ease: "power2.out",
          delay: 0.5,
        });
        if (comparatifRef.current.material?.uniforms?.uFallOffThreshold) {
          gsap.to(comparatifRef.current.material.uniforms.uFallOffThreshold, {
            value: 0.4,
            duration: 0.75,
            ease: "power2.inOut",
            delay: 0.5,
          });
        }
      } else {
        // Cacher le cube quand on quitte TechnologyHub
        gsap.to(comparatifRef.current.scale, {
          x: 0,
          y: 0,
          z: 0,
          duration: 0.5,
          ease: "power2.out",
        });
      }
    }
  }, [currentState]);

  useGSAP(
    () => {
      if (groupRef.current && currentState === "TechnologyHub") {
        const tl = gsap.timeline();
        let meshSelected;

        groupRef.current.children.forEach((mesh) => {
          // Ignorer les éléments sans matériau (groupes HTML, etc.)
          if (!mesh.material || !mesh.material.uniforms) {
            return;
          }

          // Ignorer le cube comparatif (géré par useEffect)
          if (mesh.module === "comparatif") {
            return;
          }

          // Les autres hologrammes se cachent
          tl.to(
            mesh.material.uniforms.uFallOffThreshold,
            {
              value: 0,
              duration: 0.5,
              ease: "power2.out",
            },
            "<",
          ).to(
            mesh.scale,
            {
              x: 0,
              y: 0,
              z: 0,
              duration: 0.5,
              ease: "power2.out",
            },
            ">",
          );

          if (currentModule === mesh.module) {
            meshSelected = mesh;
          }
        });

        // Afficher le mesh sélectionné
        if (meshSelected && meshSelected.material?.uniforms) {
          tl.to(
            meshSelected.scale,
            {
              x: 0.3,
              y: 0.3,
              z: 0.3,
              duration: 0.75,
              ease: "power2.out",
            },
          ).to(
            meshSelected.material.uniforms.uFallOffThreshold,
            {
              value: 0.4,
              duration: 0.75,
              ease: "power2.inOut",
            },
            ">",
          );
        }
      }
    },
    { dependencies: [currentModule, currentState] },
  );

  return (
    <group {...props} ref={groupRef}>
      <mesh
        module={"holoA"}
        geometry={nodes.lens001.geometry}
        rotation={[5, 3.2, 2]}
        scale={0}
      >
        <HologramMaterial power={0.5} ></HologramMaterial>
      </mesh>
      <mesh
        module={"holoB"}
        geometry={nodes.lens002.geometry}
        rotation={[5, 3.2, 2]}
        scale={0}
      >
        <HologramMaterial power={0.5} ></HologramMaterial>
      </mesh>
      <mesh
        module={"holoC"}
        geometry={nodes.lens003.geometry}
        rotation={[5, -3.2, 2]}
        scale={0}
      >
        <HologramMaterial power={0.5}></HologramMaterial>
      </mesh>
      <mesh
        module={"holoD"}
        geometry={nodes.lens004.geometry}
        rotation={[5, -3.2, 2]}
        scale={0}
      >
        <HologramMaterial power={0.5}></HologramMaterial>
      </mesh>
      <group position={[0, -0.5, 0]}>
        <mesh
          ref={comparatifRef}
          module={"comparatif"}
          rotation={[0.3, 0.7, 0.2]}
          scale={[1.5, 1.5, 1.5]}
          onClick={handleComparatifClick}
        >
          <boxGeometry args={[4, 4, 4]} />
          <HologramMaterial power={1.5}></HologramMaterial>
        </mesh>

        {/* Texte "Positioning" au centre du cube - visible uniquement dans TechnologyHub */}
        {currentState === "TechnologyHub" && (
          <Html
            position={[0, 0, 0]}
            center
            distanceFactor={8}
            style={{
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            <div
              style={{
                color: '#fff',
                fontSize: '10px',
                fontWeight: 'bold',
                textAlign: 'center',
                textShadow: '0 0 10pxrgb(239, 239, 239), 0 0 20pxrgb(251, 251, 251)',
                fontFamily: 'Arial, sans-serif',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                position: 'relative',
              }}
            >
              Positioning
              {/* Pastille en cercle */}
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: '8px',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  border: '1px solid white',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 0 5px rgba(255, 255, 255, 0.4)',
                  animation: 'pulse 1s ease-in-out infinite',
                }}
              />
            </div>
            <style>
              {`
                @keyframes pulse {
                  0% {
                    box-shadow: 0 0 5px rgba(255, 255, 255, 0.4);
                    outline: 1px solid rgba(255, 255, 255, 0);
                    outline-offset: 1px;
                  }
                  50% {
                    box-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
                    outline: 1px solid rgba(255, 255, 255, 0.5);
                    outline-offset: 2px;
                  }
                  100% {
                    box-shadow: 0 0 5px rgba(255, 255, 255, 0.4);
                    outline: 1px solid rgba(255, 255, 255, 0);
                    outline-offset: 3px;
                  }
                }
              `}
            </style>
          </Html>
        )}
      </group>
    </group>
  );
}
